import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Trophy, Medal, Award, TrendingUp, Building2, Loader2 } from "lucide-react";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const podioColors = {
  1: { bg: "linear-gradient(135deg, #FFD700 0%, #FFAA00 100%)", text: "#7C2D12", label: "Campeão" },
  2: { bg: "linear-gradient(135deg, #C0C0C0 0%, #909090 100%)", text: "#1F2937", label: "2º lugar" },
  3: { bg: "linear-gradient(135deg, #CD7F32 0%, #9A5B25 100%)", text: "#FFF",     label: "3º lugar" },
};

function PodioCard({ row, isMe }) {
  const pos = row.posicao;
  const c = podioColors[pos] || podioColors[3];
  const Icon = pos === 1 ? Trophy : pos === 2 ? Medal : Award;
  const order = pos === 1 ? "md:order-2" : pos === 2 ? "md:order-1" : "md:order-3";
  const heightAdjust = pos === 1 ? "md:scale-110 md:-mt-2" : "md:scale-100";
  return (
    <Card
      className={`p-5 ${order} ${heightAdjust} relative overflow-hidden ${isMe ? "ring-2 ring-orange-500" : ""}`}
      data-testid={`podio-${pos}`}
      style={{ background: c.bg, color: c.text }}
    >
      <div className="absolute top-3 right-3 opacity-30"><Icon size={64} /></div>
      <div className="relative">
        <div className="flex items-center gap-2 text-xs uppercase font-bold opacity-90 mb-1">
          <Icon size={14} /> {c.label}
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-14 h-14 rounded-full bg-white/30 flex items-center justify-center text-2xl font-bold backdrop-blur" style={{ color: c.text }}>
            {row.iniciais}
          </div>
          <div>
            <div className="text-lg font-display font-bold leading-tight">{row.vendedor_nome}</div>
            <div className="text-xs opacity-80">{row.vendedor_role}{row.unidade_nome && ` · ${row.unidade_nome}/${row.unidade_estado}`}</div>
          </div>
        </div>
        <div className="bg-white/20 rounded p-2 backdrop-blur">
          <div className="text-[10px] uppercase opacity-80">% Meta atingida</div>
          <div className="text-3xl font-bold leading-none">{row.pct_meta}%</div>
        </div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <span>Comissão: <strong>{fmt(row.valor_total)}</strong></span>
          <span>{row.qtd_contratos} contratos</span>
        </div>
        {isMe && <Badge className="absolute -top-1 -left-1 bg-orange-600 text-white text-[10px]">VOCÊ</Badge>}
      </div>
    </Card>
  );
}

export default function RankingVendedores() {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [unidades, setUnidades] = useState([]);
  const [unidade, setUnidade] = useState("__todas__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const podeFiltrarUnidade = user && (user.role === "diretor" || user.role === "supervisao") && user.escopo === "geral";

  useEffect(() => {
    if (podeFiltrarUnidade) {
      api.get("/comercial/unidades").then((r) => setUnidades(r.data || []));
    }
  }, [podeFiltrarUnidade]);

  useEffect(() => {
    setLoading(true);
    const params = { mes, ano };
    if (unidade !== "__todas__") params.unidade_id = unidade;
    api.get("/comercial/comissoes/ranking", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mes, ano, unidade]);

  const topo = data?.items?.slice(0, 3) || [];
  const resto = data?.items?.slice(3) || [];

  return (
    <div className="space-y-5" data-testid="ranking-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp size={26} style={{ color: "#F97316" }} />
          <div>
            <h2 className="text-2xl font-display font-bold" style={{ color: "#1B3A6B" }}>Ranking do Mês</h2>
            <p className="text-xs text-muted-foreground">Desempenho competitivo — {MESES[mes - 1]}/{ano}</p>
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs uppercase">Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger className="w-[110px]" data-testid="rank-mes"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger className="w-[100px]" data-testid="rank-ano"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                  <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {podeFiltrarUnidade && (
            <div>
              <Label className="text-xs uppercase">Unidade</Label>
              <Select value={unidade} onValueChange={setUnidade}>
                <SelectTrigger className="w-[170px]" data-testid="rank-unidade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todas__">Todas</SelectItem>
                  {unidades.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} ({u.estado})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {data?.minha_posicao && (
        <Card className="p-3 border-orange-300 bg-orange-50">
          <div className="flex items-center gap-2 text-sm">
            <Trophy size={16} className="text-orange-600" />
            <span>Sua posição neste mês: <strong className="text-orange-700 text-lg">{data.minha_posicao}º</strong> de {data.total}</span>
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando ranking…</Card>
      ) : data && data.items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhum vendedor com dados neste período.</Card>
      ) : (
        <>
          {topo.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start" data-testid="podio">
              {topo.map((row) => <PodioCard key={row.vendedor_id} row={row} isMe={user?.id === row.vendedor_id} />)}
            </div>
          )}

          {resto.length > 0 && (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">Pos</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead className="w-[180px]">% Meta</TableHead>
                    <TableHead className="text-center">Fechados</TableHead>
                    <TableHead className="text-right">Comissão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resto.map((row) => {
                    const isMe = user?.id === row.vendedor_id;
                    return (
                      <TableRow key={row.vendedor_id} className={isMe ? "bg-orange-50" : ""} data-testid={`rank-row-${row.vendedor_id}`}>
                        <TableCell className="text-center font-bold text-muted-foreground">{row.posicao}º</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-xs font-bold">{row.iniciais}</div>
                            <div>
                              <div className="font-medium">{row.vendedor_nome}</div>
                              <div className="text-[11px] text-muted-foreground">{row.vendedor_role}</div>
                            </div>
                            {isMe && <Badge className="bg-orange-600 text-white text-[10px]">VOCÊ</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{row.unidade_nome ? <Badge variant="outline" className="gap-1"><Building2 size={11} />{row.unidade_nome}</Badge> : "—"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={Math.min(100, row.pct_meta)} className="h-2 flex-1" />
                            <span className="text-xs font-bold w-12 text-right">{row.pct_meta}%</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-1">{row.fechamentos_realizados}/{row.meta_fechamentos || "—"} fechamentos</div>
                        </TableCell>
                        <TableCell className="text-center">{row.qtd_contratos}</TableCell>
                        <TableCell className="text-right font-bold" style={{ color: "#1B3A6B" }}>{fmt(row.valor_total)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

          <p className="text-[11px] text-muted-foreground">
            Ordenação: % meta atingida → Comissão gerada → Quantidade de contratos. Iniciais usadas no lugar de avatar (campo ainda não cadastrado).
          </p>
        </>
      )}
    </div>
  );
}
